type Company = {
  name: string;
  domain: string;
  url: string;
  tagline: string;
  category: string;
  icon: string;
  featured?: boolean;
};

const COMPANIES: Company[] = [
  {
    name: "HostPepper",
    domain: "hostpepper.com",
    url: "https://hostpepper.com",
    tagline: "Fast, reliable hosting and cloud infrastructure for growing businesses.",
    category: "Hosting & Cloud",
    icon: "ri-server-line",
    featured: true,
  },
  {
    name: "Hakker.Ai",
    domain: "hakker.ai",
    url: "https://hakker.ai",
    tagline: "Applied AI and automation for security, ops and enterprise workflows.",
    category: "Artificial Intelligence",
    icon: "ri-robot-2-line",
    featured: true,
  },
  {
    name: "Hakk.Ai",
    domain: "hakk.ai",
    url: "https://hakk.ai",
    tagline: "AI-native tools and services for modern technology teams.",
    category: "Artificial Intelligence",
    icon: "ri-brain-line",
  },
  {
    name: "GaonTek",
    domain: "gaontek.com",
    url: "https://gaontek.com",
    tagline: "Systems integration and technology deployment for enterprise environments.",
    category: "Integration & Deployment",
    icon: "ri-tools-line",
  },
  {
    name: "Teknyq",
    domain: "teknyq.com",
    url: "https://teknyq.com",
    tagline: "Boutique technology services and consulting for complex environments.",
    category: "Consulting",
    icon: "ri-briefcase-4-line",
  },
  {
    name: "Omega Sector Security",
    domain: "omegasectorsecurity.com",
    url: "https://omegasectorsecurity.com",
    tagline: "High-tech physical security, surveillance and access control specialists.",
    category: "Security",
    icon: "ri-shield-star-line",
    featured: true,
  },
  {
    name: "Techy Bootcamp",
    domain: "techybootcamp.com",
    url: "https://techybootcamp.com",
    tagline: "Career-track training for the next generation of technology professionals.",
    category: "Training & Education",
    icon: "ri-graduation-cap-line",
  },
  {
    name: "Intl Couriers",
    domain: "intlcouriers.com",
    url: "https://intlcouriers.com",
    tagline: "International courier and logistics solutions for time-critical shipments.",
    category: "Logistics",
    icon: "ri-truck-line",
  },
  {
    name: "Clear Beaches",
    domain: "clearbeaches.org",
    url: "https://clearbeaches.org",
    tagline: "Environmental initiative dedicated to cleaner, safer beaches worldwide.",
    category: "Non-profit",
    icon: "ri-leaf-line",
  },
];

export default function Companies() {
  return (
    <section id="ventures" className="relative py-20 md:py-32 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary-600 font-semibold mb-4">
              <span className="w-8 h-px bg-primary-500"></span>
              Companies & Ventures
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight tracking-tight">
              A portfolio built to <span className="text-primary-600">solve real problems.</span>
            </h2>
            <p className="mt-4 md:mt-6 text-base md:text-lg text-foreground-700 leading-relaxed">
              From hosting and AI to security and training, each brand tackles a
              distinct piece of the modern technology stack.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-external-link-line w-4 h-4 flex items-center justify-center text-primary-600"></i>
            <span>Click any card to visit the site</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {COMPANIES.map((c) => (
            <a
              key={c.domain}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={`group relative overflow-hidden rounded-lg border p-6 md:p-7 flex flex-col transition-all duration-300 cursor-pointer ${
                c.featured
                  ? "bg-foreground-950 border-foreground-900 hover:border-primary-400"
                  : "bg-background-100 border-background-200 hover:border-primary-300 hover:bg-background-50"
              }`}
            >
              <div className="flex items-start justify-between mb-6">
                <div
                  className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-md transition-colors ${
                    c.featured
                      ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                      : "bg-background-50 text-primary-700 group-hover:bg-primary-500 group-hover:text-background-50 dark:group-hover:text-foreground-950"
                  }`}
                >
                  <i className={`${c.icon} text-2xl md:text-3xl`}></i>
                </div>
                <span
                  className={`w-9 h-9 flex items-center justify-center rounded-full border transition-all group-hover:rotate-[-45deg] ${
                    c.featured
                      ? "border-background-50/25 text-background-50/80 group-hover:text-primary-400 group-hover:border-primary-400"
                      : "border-background-300 text-foreground-500 group-hover:text-primary-600 group-hover:border-primary-500"
                  }`}
                >
                  <i className="ri-arrow-right-up-line text-lg"></i>
                </span>
              </div>

              <div className="mt-auto">
                <span
                  className={`inline-block text-[10px] md:text-xs font-semibold uppercase tracking-widest mb-2 ${
                    c.featured ? "text-primary-400" : "text-primary-700"
                  }`}
                >
                  {c.category}
                </span>
                <h3
                  className={`font-heading text-xl md:text-2xl font-bold leading-tight tracking-tight ${
                    c.featured ? "text-background-50" : "text-foreground-950"
                  }`}
                >
                  {c.name}
                </h3>
                <p
                  className={`mt-2 text-sm md:text-base leading-relaxed ${
                    c.featured ? "text-background-100/80" : "text-foreground-700"
                  }`}
                >
                  {c.tagline}
                </p>
                <div
                  className={`mt-5 pt-4 border-t flex items-center gap-2 text-xs md:text-sm font-mono ${
                    c.featured
                      ? "border-foreground-800 text-background-100/70"
                      : "border-background-200 text-foreground-600"
                  }`}
                >
                  <i className="ri-links-line w-4 h-4 flex items-center justify-center"></i>
                  <span className="truncate">{c.domain}</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}